import type { Intervention } from '@planet-sdk/common';

import { useEffect } from 'react';
import {
  useInterventionStore,
  useSingleProjectStore,
  useViewStore,
} from '../stores';
import { useRouter } from 'next/router';
import { useLocale } from 'next-intl';
import { FIRST_SITE_INDEX, hasNoSites, isString } from '../utils/projectV2';

const getInterventionByHid = (
  interventions: Intervention[] | null,
  hid: string | number
): Intervention | undefined => {
  return interventions?.find((intervention) => intervention.hid === hid);
};

export const useInitializeIntervention = () => {
  const locale = useLocale();
  const router = useRouter();
  const { site: requestedSite, ploc: requestedIntervention } = router.query;
  const hasRequestedBothTogether = Boolean(
    requestedSite && requestedIntervention
  );
  // store: state
  const currentPage = useViewStore((state) => state.page);
  const singleProject = useSingleProjectStore((state) => state.singleProject);
  const selectedIntervention = useInterventionStore(
    (state) => state.selectedIntervention
  );
  const selectedSite = useSingleProjectStore((state) => state.selectedSite);
  const interventions = useInterventionStore((state) => state.interventions);
  // store: action
  const clearInterventionStates = useInterventionStore(
    (state) => state.clearInterventionStates
  );
  const selectInterventionSyncUrl = useInterventionStore(
    (state) => state.selectInterventionSyncUrl
  );
  const selectSiteAndSyncUrl = useSingleProjectStore(
    (state) => state.selectSiteAndSyncUrl
  );
  const projectSlug = singleProject?.slug ?? '';
  const projectSites = singleProject?.sites ?? [];
  const hasInterventions = Boolean(interventions?.length);

  /**
   * Initialize intervention selection for project details page.
   * Selects an intervention only when:
   * - Router is ready
   * - We are on project-details
   * - Interventions are loaded
   * - No site or intervention is already selected
   * - URL explicitly requests an intervention
   * - URL does not request both site and intervention together
   */
  useEffect(() => {
    if (!router.isReady) return;
    if (currentPage === 'project-list') return;
    if (!hasInterventions) return;
    if (selectedSite !== null) return;
    if (hasRequestedBothTogether) return;
    if (!isString(requestedIntervention)) return;
    if (selectedIntervention !== null) return;

    const intervention = getInterventionByHid(
      interventions,
      requestedIntervention
    );

    if (intervention) {
      selectInterventionSyncUrl(intervention, locale, projectSlug, router);
    } else {
      // Invalid ploc: fall back to the first site, or none when the project has
      // no selectable sites (all geometries null), matching useInitializeSingleProject.
      selectSiteAndSyncUrl(
        hasNoSites(projectSites) ? null : FIRST_SITE_INDEX,
        locale,
        router
      );
    }
  }, [
    router.isReady,
    currentPage,
    selectedIntervention,
    interventions,
    selectedSite,
  ]);

  useEffect(() => {
    if (currentPage === 'project-list') clearInterventionStates();
  }, [currentPage]);
};
